"use client";

import { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card_v3";
import { cn } from "lib/utils";
import localization from "services/localization";

const BASE_COLORS = ["#3b82f6", "#10b981", "#f59e0b"];

const LABELS = {
  teacher: "Teachers",
  student: "Students",
  administrative: "Admin",
};

export default function RoleDistributionChart({ messages, theme ,users,departments,selectedUser, selectedRole}) {
  const card_bg =
    theme === "dark" ? "bg-[var(--card-bg)] border-border" : "bg-card border-border";

const usersMap = useMemo(() => {
  const map = {};
  users.forEach((u) => (map[u.id] = u));
  return map;
}, [users]);

const departmentsMap = useMemo(() => {
      const map = {};
      departments.forEach((d) => (map[d.id] = d));
      return map;
  }, [departments]);


const generateColors = (count) => {
  const colors = [...BASE_COLORS];

  if (count <= BASE_COLORS.length) {
    return colors.slice(0, count);
  }

  const extraNeeded = count - BASE_COLORS.length;
  const hueStep = 360 / extraNeeded;

  for (let i = 0; i < extraNeeded; i++) {
    const hue = Math.round(i * hueStep);
    colors.push(`hsl(${hue}, 60%, 55%)`);
  }

  return colors;
};

  const data = useMemo(() => {
   if (!messages?.length) return [];

    // 1️⃣ Single user selected → group by department
    if (selectedUser && selectedUser !== "all") {
      const counts = {};
      messages.forEach(m => {
        const depId = m.department_id || "Unknown";
        counts[depId] = (counts[depId] || 0) + (m.metrics?.total_messages || 0);
      });
      debugger
      const entries = Object.entries(counts);
      const colors = generateColors(entries.length);
      return entries.map(([name, value], idx) => ({
        name: departmentsMap[name]?.name || "Unknown",
        value,
       color: colors[idx]
      }));
    }

    // 2️⃣ Single role selected → group by users
  if (selectedRole && selectedRole !== "all") {
  const counts = {};

  messages.forEach((m) => {
    const user = usersMap[m.user_id];
    if (user) {
      counts[user.name] =
        (counts[user.name] || 0) + (m.metrics?.total_messages || 0);
    }
  });

  // 👉 convert to array + sort
  let entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  // 👉 take top 5
  const TOP_N = 5;
  const topEntries = entries.slice(0, TOP_N);
  const rest = entries.slice(TOP_N);

  // 👉 sum others
  const othersValue = rest.reduce((sum, [, v]) => sum + v, 0);

  if (othersValue > 0) {
    topEntries.push(["Others", othersValue]);
  }

  const colors = generateColors(topEntries.length);

  return topEntries.map(([name, value], idx) => ({
    name,
    value,
    color: colors[idx],
  }));
}

    // 3️⃣ Default → all roles
    const roleCounts = { teacher: 0, student: 0, administrative: 0 };
    messages.forEach(m => {
      const user = usersMap[m.user_id];
      const count = m.metrics?.total_messages || 0;
      if (user?.role === "teacher") roleCounts.teacher += count;
      else if (user?.role === "student") roleCounts.student += count;
      else if (user?.role === "administrative") roleCounts.administrative += count;
    });
    const LABELS = { teacher: "Teachers", student: "Students", administrative: "Admin" };
    const COLORS_ROLE = { teacher: "#3b82f6", student: "#10b981", administrative: "#f59e0b" };
    return Object.entries(roleCounts).map(([role, value]) => ({
      name: LABELS[role],
      value,
      color: COLORS_ROLE[role],
    }));
  }, [messages, usersMap, selectedUser, selectedRole]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const total = data.reduce((s, item) => s + item.value, 0);
    const pct = ((d.value / total) * 100).toFixed(1);
    return (
      <div
        className={cn(
          "rounded-lg border p-3 shadow-lg",
          theme === "dark" ? "bg-[#1a2332] border-border text-foreground" : "bg-card border-border text-foreground"
        )}
      >
        <p className="text-sm font-medium">{d.name}</p>
        <p className="text-xs text-muted-foreground">
          {localization.messages || "Messages"}: <span className="font-medium text-foreground">{d.value}</span> ({pct}%)
        </p>
      </div>
    );
  };

  if (!messages?.length) {
  return (
    <Card className={cn(card_bg)}>
      <CardContent className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
        {localization.no_data || "No data"}
      </CardContent>
    </Card>
  );
}

  return (
    <Card className={cn(card_bg)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-foreground">
         {selectedUser && selectedUser !== "all"
            ? localization.usage_by_department || "Usage by Department"
            : selectedRole && selectedRole !== "all"
            ? localization.usage_by_user || "Usage by User"
            : localization.usage_by_role || "Usage by Role"}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
                stroke="none"
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: "12px" }}
                formatter={(value) => (
                  <span className="text-muted-foreground">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
